import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { Guard } from "@/components/Guard";
import { Users, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, Legend,
} from "recharts";
import { format, parseISO, startOfMonth } from "date-fns";
import { fr } from "date-fns/locale";

export const Route = createFileRoute("/")({ component: Dashboard });

function Dashboard() {
  return (
    <Guard module="dashboard" level="read">
      <DashboardContent />
    </Guard>
  );
}

function DashboardContent() {
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const [{ count: memberCount }, { data: tx }, { data: memberships }] = await Promise.all([
        supabase
          .from("members")
          .select("*", { count: "exact", head: true })
          .eq("archived", false),
        supabase.from("transactions").select("date,amount,kind"),
        supabase.from("memberships").select("member_id,season,paid_at"),
      ]);
      return { memberCount: memberCount ?? 0, tx: tx ?? [], memberships: memberships ?? [] };
    },
  });

  const tx = (stats?.tx ?? []) as Array<{ date: string; amount: number; kind: string }>;
  const totalIncome = tx.filter((t) => t.kind === "income").reduce((s: number, t) => s + Number(t.amount), 0);
  const totalExpense = tx.filter((t) => t.kind === "expense" || t.kind === "reimbursement").reduce((s: number, t) => s + Number(t.amount), 0);
  const balance = totalIncome - totalExpense;

  // Adhérents par saison (membres distincts par saison, basé sur memberships)
  const bySeason = new Map<string, Set<string>>();
  ((stats?.memberships ?? []) as Array<{ member_id: string; season: string | null; paid_at: string | null }>).forEach((m) => {
    if (!m.season || !m.member_id) return;
    const season = String(m.season).trim();
    if (!season) return;
    if (!bySeason.has(season)) bySeason.set(season, new Set());
    bySeason.get(season)!.add(m.member_id);
  });
  const membersChart = Array.from(bySeason.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([season, ids]) => ({ season, adhérents: ids.size }));

  // Trésorerie cumulée par mois
  const byMonth = new Map<string, { income: number; expense: number }>();
  tx.forEach((t) => {
    const key = format(startOfMonth(parseISO(t.date)), "yyyy-MM");
    const cur = byMonth.get(key) ?? { income: 0, expense: 0 };
    if (t.kind === "income") cur.income += Number(t.amount);
    else if (t.kind === "expense" || t.kind === "reimbursement") cur.expense += Number(t.amount);
    byMonth.set(key, cur);
  });
  let cumul = 0;
  const treasuryChart = Array.from(byMonth.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => {
    cumul += v.income - v.expense;
    return { mois: format(parseISO(k + "-01"), "MMM yy", { locale: fr }), Solde: Math.round(cumul * 100) / 100, Recettes: v.income, Dépenses: v.expense };
  });

  const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

  return (
    <>
      <PageHeader title="Tableau de bord" description="Vue d'ensemble de votre association" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Adhérents" value={String(stats?.memberCount ?? 0)} accent />
        <StatCard icon={TrendingUp} label="Recettes" value={fmt(totalIncome)} />
        <StatCard icon={TrendingDown} label="Dépenses" value={fmt(totalExpense)} />
        <StatCard icon={Wallet} label="Solde" value={fmt(balance)} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Adhérents par saison</h3>
          {membersChart.length === 0 ? (
            <EmptyChart>Aucune adhésion enregistrée</EmptyChart>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={membersChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="season" stroke="currentColor" fontSize={12} />
                <YAxis stroke="currentColor" fontSize={12} />
                <Tooltip />
                <Bar dataKey="adhérents" fill="oklch(0.45 0.18 255)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Évolution de la trésorerie</h3>
          {treasuryChart.length === 0 ? (
            <EmptyChart>Aucun mouvement enregistré</EmptyChart>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={treasuryChart}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.45 0.18 255)" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="oklch(0.45 0.18 255)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="mois" stroke="currentColor" fontSize={12} />
                <YAxis stroke="currentColor" fontSize={12} />
                <Tooltip />
                <Area type="monotone" dataKey="Solde" stroke="oklch(0.45 0.18 255)" fillOpacity={1} fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-display text-lg font-semibold mb-4">Recettes vs Dépenses mensuelles</h3>
        {treasuryChart.length === 0 ? (
          <EmptyChart>Aucun historique</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={treasuryChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="mois" stroke="currentColor" fontSize={12} />
              <YAxis stroke="currentColor" fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Recettes" fill="oklch(0.65 0.15 150)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Dépenses" fill="oklch(0.6 0.22 25)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </>
  );
}

function StatCard({ icon: Icon, label, value, accent }: { icon: any; label: string; value: string; accent?: boolean }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${accent ? "bg-accent/15 text-accent-foreground" : "bg-muted"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 font-display text-2xl font-semibold">{value}</div>
    </Card>
  );
}

function EmptyChart({ children }: { children: React.ReactNode }) {
  return <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">{children}</div>;
}
